import React, { useState, useRef, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  TextField, 
  IconButton, 
  Avatar, 
  Fab, 
  Fade, 
  CircularProgress,
  useTheme,
  useMediaQuery,
  styled
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import { getChatbotResponse, ChatMessage } from '../../services/chatbotService';

// Styled Components
const ChatContainer = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(10),
  right: theme.spacing(2),
  width: '350px',
  height: '500px',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.shape.borderRadius * 2,
  overflow: 'hidden',
  boxShadow: theme.shadows[8],
  zIndex: 1000,
  [theme.breakpoints.down('sm')]: {
    width: 'calc(100% - 32px)',
    height: '70vh',
    bottom: theme.spacing(8),
  },
}));

const ChatHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const MessageList = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(2),
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  backgroundColor: theme.palette.background.default,
}));

const MessageBubble = styled(Box)<{ isUser: boolean }>(({ theme, isUser }) => ({
  display: 'flex',
  justifyContent: isUser ? 'flex-end' : 'flex-start',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
}));

const MessageContent = styled(Paper)<{ isUser: boolean }>(({ theme, isUser }) => ({
  padding: theme.spacing(1.5, 2),
  maxWidth: '80%',
  borderRadius: isUser 
    ? theme.spacing(2, 2, 0, 2) 
    : theme.spacing(2, 2, 2, 0),
  backgroundColor: isUser 
    ? theme.palette.primary.main 
    : theme.palette.background.paper,
  color: isUser 
    ? theme.palette.primary.contrastText 
    : theme.palette.text.primary,
  boxShadow: theme.shadows[1],
  wordBreak: 'break-word',
}));

const InputContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

const PartnerChatbot: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: 'Xin chào! Tôi là trợ lý ảo của Bookdi. Tôi có thể giúp bạn tìm hiểu về quy trình đăng ký đối tác, các gói dịch vụ, và giải đáp thắc mắc của bạn.',
      sender: 'bot',
      timestamp: new Date()
    }
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [conversationHistory, setConversationHistory] = useState<ChatMessage[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // URL của hình ảnh trợ lý - bạn có thể thay đổi URL này bất cứ lúc nào
  const assistantAvatarUrl = "https://asset.cloudinary.com/diqgysqbr/99f7b8b89aa958e7ec62cd248284ceb5"; // Thay thế bằng URL thực tế của hình ảnh

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSendMessage = async () => {
    if (!input.trim()) return;

    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      text: input,
      sender: 'user',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    
    // Update conversation history for context
    const newUserChatMessage: ChatMessage = {
      role: 'user',
      content: input
    };
    const updatedHistory = [...conversationHistory, newUserChatMessage];
    setConversationHistory(updatedHistory);
    
    setInput('');
    setIsLoading(true);

    try {
      console.log('Chuẩn bị gửi tin nhắn đến chatbot:', input);
      console.log('Lịch sử hội thoại:', updatedHistory.length, 'tin nhắn');
      
      // Get AI response
      const response = await getChatbotResponse(input, conversationHistory);
      console.log('Nhận phản hồi từ chatbot:', response);
      
      // Add bot message
      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: response,
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botMessage]);
      
      // Update conversation history
      setConversationHistory([
        ...updatedHistory, 
        { role: 'assistant', content: response }
      ]);
    } catch (error) {
      console.error('Error getting AI response:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: 'Xin lỗi, tôi đang gặp sự cố. Vui lòng thử lại sau hoặc liên hệ hỗ trợ qua email info@bookdi.net.',
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      {/* Chat Button */}
      <Fab
        color="primary"
        aria-label="chat"
        onClick={toggleChat}
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          zIndex: 1000,
          boxShadow: theme.shadows[6],
        }}
      >
        {isOpen ? <CloseIcon /> : <ChatIcon />}
      </Fab>

      {/* Chat Window */}
      <Fade in={isOpen}>
        <ChatContainer>
          <ChatHeader>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ backgroundColor: 'primary.dark' }}>
                {assistantAvatarUrl ? (
                  <img 
                    src={assistantAvatarUrl} 
                    alt="AI Assistant" 
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                  />
                ) : (
                  <SmartToyIcon />
                )}
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  Trợ lý Bookdi
                </Typography>
                <Typography variant="caption">
                  Hỗ trợ bởi AI
                </Typography>
              </Box>
            </Box>
            {isMobile && (
              <IconButton size="small" onClick={toggleChat} color="inherit">
                <CloseIcon />
              </IconButton>
            )}
          </ChatHeader>

          <MessageList>
            {messages.map((msg) => (
              <MessageBubble key={msg.id} isUser={msg.sender === 'user'}>
                {msg.sender === 'bot' && (
                  <Avatar sx={{ 
                    bgcolor: 'primary.main',
                    width: 36, 
                    height: 36
                  }}>
                    {assistantAvatarUrl ? (
                      <img 
                        src={assistantAvatarUrl} 
                        alt="AI Assistant" 
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                      />
                    ) : (
                      <SmartToyIcon fontSize="small" />
                    )}
                  </Avatar>
                )}
                <MessageContent isUser={msg.sender === 'user'}>
                  <Typography variant="body2">
                    {msg.text}
                  </Typography>
                </MessageContent>
                {msg.sender === 'user' && (
                  <Avatar sx={{ 
                    bgcolor: 'secondary.main',
                    width: 36, 
                    height: 36
                  }}>
                    <PersonIcon fontSize="small" />
                  </Avatar>
                )}
              </MessageBubble>
            ))}
            {isLoading && (
              <MessageBubble isUser={false}>
                <Avatar sx={{ 
                  bgcolor: 'primary.main',
                  width: 36, 
                  height: 36
                }}>
                  {assistantAvatarUrl ? (
                    <img 
                      src={assistantAvatarUrl} 
                      alt="AI Assistant" 
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                    />
                  ) : (
                    <SmartToyIcon fontSize="small" />
                  )}
                </Avatar>
                <MessageContent isUser={false}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} color="inherit" />
                    <Typography variant="body2">Đang nhập...</Typography>
                  </Box>
                </MessageContent>
              </MessageBubble>
            )}
            <div ref={messagesEndRef} />
          </MessageList>

          <InputContainer>
            <TextField
              fullWidth
              placeholder="Nhập tin nhắn của bạn..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              variant="outlined"
              size="small"
              disabled={isLoading}
              autoComplete="off"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3
                }
              }}
            />
            <IconButton 
              color="primary" 
              onClick={handleSendMessage}
              disabled={isLoading || !input.trim()}
              sx={{ 
                backgroundColor: (theme) => 
                  input.trim() 
                    ? alpha(theme.palette.primary.main, 0.1) 
                    : 'transparent'
              }}
            >
              <SendIcon />
            </IconButton>
          </InputContainer>
        </ChatContainer>
      </Fade>
    </>
  );
};

// Hàm helper để làm mờ màu nền
const alpha = (color: string, opacity: number): string => {
  // Đơn giản hóa - trong thực tế bạn sẽ cần một hàm chuyển đổi màu đầy đủ
  return color + Math.round(opacity * 255).toString(16).padStart(2, '0');
};

export default PartnerChatbot; 